// App entry — wires global styles, TanStack Query's QueryClient, and mounts
// the router-aware <App />.
//
// QueryClient defaults (A2 spike protection — see plan U4):
//   - `staleTime: 30s` for daily-data queries (weight_log, daily_log,
//     workout_log) — change at human timescales; UX wants fresh values on
//     tab switch.
//   - `staleTime: 10min` for slow-changing queries (profile, daily_targets,
//     speediance_config, speediance_mapping, exercise_weights) — change at
//     session-to-session or week-to-week timescales.
//   - `refetchOnWindowFocus: true` only honored when stale. Prevents a
//     misconfigured `refetchInterval` or a tab-switch-spam pattern from
//     burning through D1's 25k reads/day free tier.
//
// Per-query overrides happen where queries are introduced; here we set the
// *global default* to the daily-data tier (30s) because most queries the app
// issues are in that category. Slow-changing queries opt in explicitly via
// `staleTime: STALE_TIME_SLOW`. Both tiers live in `lib/query.ts` (a leaf
// module) so importing them never creates a cycle through this entry file.

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { STALE_TIME_DAILY } from './lib/query';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME_DAILY,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
